import { useEffect, useRef, type RefObject } from "react";

interface SeatModalKeys {
  open: boolean;
  /** `false`, докато въпросът е отворен — буквите и Esc не стигат до търсачката под него. */
  typing: boolean;
  inputRef: RefObject<HTMLInputElement | null>;
  /** Esc: изчиства търсенето (фокус в полето с текст), иначе стъпка назад / затваря. */
  onEscape: () => void;
  onEnter: () => void;
  onMove: (delta: 1 | -1) => void;
  /** ←/→ във въпроса (не в търсачката): „Една сметка" / „Отделна сметка за всяка маса". */
  onSide: (delta: 1 | -1) => void;
  onType: (character: string) => void;
  onClearQuery: () => void;
}

/**
 * Клавиатурата на „Настани" (capture фаза — преди TableList под модала):
 * ↑/↓ местят избора, ←/→ във въпроса сменят сметките (една / отделна за
 * всяка маса), Enter натиска основния бутон, Esc — назад/затваря,
 * буква/цифра отива в търсачката. Esc ВИНАГИ с preventDefault — иначе
 * минава и глобалното „назад" на общия терминал (SharedAccountGate).
 * Callback-ите се четат от ref — слушателят не се пре-закача на всеки рендер.
 */
export const useSeatModalKeys = (keys: SeatModalKeys) => {
  const latest = useRef(keys);
  useEffect(() => {
    latest.current = keys;
  });

  const { open } = keys;
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      const current = latest.current;
      const input = current.inputRef.current;
      const inInput = event.target === input;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (current.typing && inInput && input && input.value !== "") current.onClearQuery();
        else current.onEscape();
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        current.onEnter();
        return;
      }
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        current.onMove(event.key === "ArrowDown" ? 1 : -1);
        return;
      }
      if (!current.typing && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
        event.preventDefault();
        current.onSide(event.key === "ArrowRight" ? 1 : -1);
        return;
      }
      if (inInput || !current.typing) return;
      if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        current.onType(event.key);
        input?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open]);
};
